import { useEffect } from 'react';
import { useIntl } from 'react-intl';

import { CsvImporter } from '@/components/common/CsvImporter/CsvImporter';
import { buildInitialMapping, DELIMITER_CHARS } from '@/components/common/CsvImporter/csvImporter.helpers';
import { LOCATIONS_COLUMNS } from '@/utils/files/constants';
import { useLocationCSVUploadModalContext } from './context/LocationCSVUploadModalContext';

import type {
  CsvImporterColumn,
  CsvImporterColumnGroup,
  CsvMapping
} from '@/components/common/CsvImporter/csvImporter.helpers';

const MAX_FILENAME_LENGTH = 80;

type Props = {
  isApplying: boolean;
  onApply: (payload: {
    columnDelimiter: string;
    country?: string;
    decimalDelimiter: string;
    fileName: string;
    keysToReview: CsvMapping[];
    lineDelimiter: string;
    totalLines?: number;
  }) => void;
  onGoBack: () => void;
};

/**
 * Zone location rules, unchanged from the previous wizard:
 * a valid mode is lat+lng OR cartographyLevelId+cartographyShapeId; a half-pair
 * of either invalidates the form; radius is required only in the lat/lng mode.
 */
function hasAllRequiredFields(mapping: CsvMapping[]): boolean {
  const requiredKeys = LOCATIONS_COLUMNS.filter(col => col.require).map(col => col.key);
  const mappedKeys = mapping.map(entry => entry.key);

  const hasLat = mappedKeys.includes('latitude');
  const hasLng = mappedKeys.includes('longitude');
  const hasLatLng = hasLat && hasLng;

  const hasCartoLevel = mappedKeys.includes('cartographyLevelId');
  const hasCartoShape = mappedKeys.includes('cartographyShapeId');
  const hasCartography = hasCartoLevel && hasCartoShape;

  const hasValidMode = hasLatLng || hasCartography;
  const hasInvalidLatLng = hasLat !== hasLng;
  const hasInvalidCarto = hasCartoLevel !== hasCartoShape;

  return (
    requiredKeys.every(key => {
      if (['latitude', 'longitude'].includes(key)) {
        return hasLatLng || hasCartography;
      }
      if (['cartographyLevelId', 'cartographyShapeId'].includes(key)) {
        return hasCartography || hasLatLng;
      }
      if (key.startsWith('radius')) {
        return !hasLatLng || mappedKeys.some(mappedKey => mappedKey.startsWith('radius'));
      }
      return mappedKeys.includes(key);
    }) &&
    hasValidMode &&
    !hasInvalidLatLng &&
    !hasInvalidCarto
  );
}

/**
 * `require` in LOCATIONS_COLUMNS is a flat boolean, but the form accepts two
 * alternative location modes. Mark as required only what the current mapping
 * still needs, so the asterisks always agree with hasAllRequiredFields.
 */
function getRequiredColumnKeys(mapping: CsvMapping[]): Set<string> {
  const mappedKeys = mapping.map(entry => entry.key);
  const hasLatLng = mappedKeys.includes('latitude') && mappedKeys.includes('longitude');
  const hasCartography = mappedKeys.includes('cartographyLevelId') && mappedKeys.includes('cartographyShapeId');
  const mappedRadius = mappedKeys.find(key => key.startsWith('radius'));

  const required = new Set<string>();

  LOCATIONS_COLUMNS.forEach(col => {
    if (['latitude', 'longitude'].includes(col.key)) {
      if (!hasCartography) {
        required.add(col.key);
      }
      return;
    }
    if (['cartographyLevelId', 'cartographyShapeId'].includes(col.key)) {
      if (!hasLatLng) {
        required.add(col.key);
      }
      return;
    }
    if (col.key.startsWith('radius')) {
      if (hasLatLng && (mappedRadius === undefined || mappedRadius === col.key)) {
        required.add(col.key);
      }
      return;
    }
    if (col.require) {
      required.add(col.key);
    }
  });

  return required;
}

export function LocationImporter({ isApplying, onApply, onGoBack }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const {
    config,
    file,
    mapping,
    preprocess,
    preprocessStatus,
    setConfig,
    setFile,
    setMapping,
    uploadedFile,
    uploadedFileStatus
  } = useLocationCSVUploadModalContext();

  const requiredColumnKeys = getRequiredColumnKeys(mapping);
  const columns: CsvImporterColumn[] = LOCATIONS_COLUMNS.map(col => ({
    key: col.key,
    label: formatMessage({ defaultMessage: col.key, id: col.cpy }),
    regexp: col.regexp as RegExp,
    required: requiredColumnKeys.has(col.key)
  }));

  const columnGroups: CsvImporterColumnGroup[] = [
    {
      columns,
      key: 'locations',
      notice: {
        text: formatMessage({
          defaultMessage:
            'Configure the columns to match your data. Define your areas using either coordinates and a radius, or a cartography level and shape.',
          id: 'locationImporter.status.info'
        })
      }
    }
  ];

  useEffect(() => {
    if (mapping.length === 0 && preprocess?.headers?.length) {
      setMapping(buildInitialMapping(columns, preprocess.headers, []));
    }
  }, [preprocess?.headers]);

  const hasLongFileNameError = (file?.name?.length || 0) > MAX_FILENAME_LENGTH;
  const hasSaveError = preprocessStatus === 'success' && uploadedFileStatus === 'error';
  // The payload needs the S3 file name, so Apply stays disabled until the upload resolves.
  const isFileUploaded = uploadedFileStatus === 'success' && !!uploadedFile?.fileName;
  const isApplyEnabled =
    !hasLongFileNameError && !hasSaveError && isFileUploaded && mapping.length > 0 && hasAllRequiredFields(mapping);

  function buildNotice() {
    if (hasLongFileNameError) {
      return {
        text: formatMessage(
          {
            defaultMessage: 'Selected file name is too long, please select another one or rename it.',
            id: 'multiFlightLocationUpload.messages.errorLongFileName'
          },
          { maxValue: MAX_FILENAME_LENGTH }
        ),
        type: 'error' as const
      };
    }
    if (hasSaveError) {
      return {
        text: formatMessage({
          defaultMessage: 'There was an error saving the file. Please try to upload it again.',
          id: 'entityFileUpload.saveFileError'
        }),
        type: 'error' as const
      };
    }
    return undefined;
  }

  function handleApply() {
    onApply({
      columnDelimiter: DELIMITER_CHARS[config.delimiter],
      decimalDelimiter: DELIMITER_CHARS[config.separator],
      fileName: uploadedFile.fileName,
      keysToReview: mapping,
      lineDelimiter: '',
      totalLines: preprocess?.dataPreview.length
    });
  }

  const notice = buildNotice();

  return (
    <CsvImporter
      applyText={formatMessage({ defaultMessage: 'Upload Locations', id: 'locationImporter.apply' })}
      columnGroups={columnGroups}
      config={config}
      file={file!}
      idPrefix="locationImporter"
      isApplyEnabled={isApplyEnabled}
      isApplying={isApplying}
      isPreprocessing={preprocessStatus === 'loading'}
      mapping={mapping}
      {...(notice ? { notice } : {})}
      onApply={handleApply}
      onConfigChange={setConfig}
      onFileChange={setFile}
      onGoBack={onGoBack}
      onMappingChange={setMapping}
      preprocess={preprocess}
      subtitle={formatMessage({
        defaultMessage: 'Configure how your document will be read in order to upload your locations correctly.',
        id: 'locationImporter.config.subtitle'
      })}
      title={formatMessage({ defaultMessage: 'File configuration and columns', id: 'poiImporter.config.title' })}
    />
  );
}
